import {
  getSessionConversationRecencyTimestamp,
  getSessionRecencyTimestamp,
  sortSessionsByRecency,
} from "./sessionOrdering";
import { resolveDisplaySessionFollowUp } from "./followUp";
import { sessionFollowUpFingerprint } from "./sessionScope";
import summaryTextIntegrity from "./summaryTextIntegrity";
import { getSummaryFreshness } from "./transcriptVersion";
import { validatePostSessionProvenance } from "./postSessionProvenance";

const { preserveCanonicalSummaryText } = summaryTextIntegrity;

export const RESUMABLE_SESSION_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
export const CONTINUITY_PACKET_MAX_SESSIONS = 3;

const CONTINUITY_REFERENCE_PATTERN =
  /\b(?:previous|prior|earlier|last|yesterday(?:'s)?|before|another)\b.{0,80}\b(?:session|conversation|discussion|chat|summary|notes?|record|transcript|follow[-\s]?up|action)\b|\b(?:session|conversation|discussion|chat|summary|notes?|record|transcript|follow[-\s]?up|action)\b.{0,80}\b(?:previous|prior|earlier|last|yesterday(?:'s)?|before|another)\b/i;
const CONTINUITY_RETRIEVAL_PATTERN =
  /\b(?:send|show|find|retrieve|restore|reconnect|continue|access|open|remember|review)\b.{0,80}\b(?:my|our|the)?\s*(?:previous|prior|earlier|last|missing|saved)?\s*(?:session|conversation|discussion|chat|summary|notes?|record|transcript|follow[-\s]?up|action)\b|\b(?:did not|didn['’]?t|never)\s+(?:receive|get|see)\b.{0,50}\b(?:summary|notes?|record|transcript|follow[-\s]?up)\b|\b(?:summary|notes?|record|transcript|follow[-\s]?up)\b.{0,50}\b(?:missing|not sent|wasn['’]?t sent|did not arrive|didn['’]?t arrive)\b/i;
const CONTINUITY_REENTRY_PATTERN =
  /\b(?:pick\s+up|continue|resume)\s+(?:right\s+)?where\s+(?:we|i)\s+left\s+off\b|\bwe\s+(?:spoke|talked|discussed)\s+about\b.{0,100}\b(?:yesterday|earlier|before|last\s+(?:time|session|week))\b|\b(?:yesterday|earlier|before|last\s+(?:time|session|week))\b.{0,100}\bwe\s+(?:spoke|talked|discussed)\s+about\b/i;
const EXACT_MOST_RECENT_REFERENCE_PATTERN =
  /\b(?:immediately\s+previous|previous|last|most\s+recent)\b.{0,30}\b(?:session|conversation|discussion|chat|summary|notes?|record|transcript|follow[-\s]?up|action)\b|\b(?:session|conversation|discussion|chat|summary|notes?|record|transcript|follow[-\s]?up|action)\b.{0,30}\b(?:immediately\s+previous|previous|last|most\s+recent|before\s+this\s+one)\b/i;
const ESCALATION_RETRIEVAL_PATTERN =
  /\btake\s+this\s+to\s+(?:your\s+)?(?:leader|manager|supervisor|coach|support)\b|\b(?:escalate|report)\s+(?:this|it|the issue|the problem)\b|\b(?:speak|talk)\s+to\s+(?:a\s+)?(?:human|leader|manager|supervisor|coach|support)\b/i;
const OPERATIONAL_RECORD_SUBJECT_PATTERN =
  /\b(?:access|account|conversation|delivery|email|follow[-\s]?up|history|login|notes?|notification|pilot(?:ing)?|record|reminder|report(?:ing)?|session|summary|transcript)\b/i;
const OPERATIONAL_FAILURE_PATTERN =
  /\b(?:cannot|can['’]?t|did\s+not|didn['’]?t|failed|failure|incorrect|lack|lost|missing|never|no\s+access|not\s+(?:available|delivered|kept|recorded|reported|received|sent|stored)|odd|problem|truncat\w*|unavailable|wrong)\b/i;
const OPERATIONAL_ROUTE_PATTERN =
  /^(?:complaint|continuity[-_]complaint|operational[-_]complaint|records[-_]complaint|support[-_]complaint)$/;

const CONTINUITY_TOPIC_STOP_WORDS = new Set([
  "about",
  "access",
  "action",
  "another",
  "before",
  "chat",
  "continue",
  "conversation",
  "discussed",
  "discussion",
  "earlier",
  "escalate",
  "find",
  "follow",
  "from",
  "have",
  "human",
  "immediately",
  "issue",
  "last",
  "leader",
  "manager",
  "missing",
  "most",
  "never",
  "notes",
  "open",
  "outside",
  "pick",
  "please",
  "previous",
  "prior",
  "problem",
  "receive",
  "received",
  "recent",
  "record",
  "remember",
  "report",
  "restore",
  "retrieve",
  "review",
  "resume",
  "send",
  "sent",
  "session",
  "show",
  "spoke",
  "summary",
  "support",
  "supervisor",
  "take",
  "talked",
  "this",
  "transcript",
  "where",
  "with",
  "yesterday",
  "your",
  "left",
]);
const NON_CURRENT_SUMMARY_STATUSES = new Set(["stale", "superseded", "rejected"]);
const FINAL_FOLLOW_UP_STATUSES = new Set(["completed", "dismissed", "cancelled", "superseded"]);

function normalizeText(value = "", maxLength = 0) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!maxLength || normalized.length <= maxLength) return normalized;
  return normalized.slice(0, maxLength).trim();
}

function normalizeStatus(value = "") {
  return normalizeText(value).toLowerCase().replace(/\s+/g, "_");
}

function toIsoTimestamp(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date.toISOString();
  }
  if (typeof value?.toMillis === "function") {
    const date = new Date(value.toMillis());
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  if (typeof value?.seconds === "number") {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function topicTokens(value = "") {
  return Array.from(new Set(
    normalizeText(value)
      .toLowerCase()
      .replace(/[’']/g, "")
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 4 && !CONTINUITY_TOPIC_STOP_WORDS.has(token))
  ));
}

function summaryCandidateWithVersion(value, versionSource = null) {
  const candidate = typeof value === "string" ? { summaryText: value } : value;
  if (!candidate || typeof candidate !== "object") return null;
  const inherited = versionSource && typeof versionSource === "object"
    ? versionSource
    : {};
  return {
    ...inherited,
    ...candidate,
    generatedAt:
      candidate.generatedAt ||
      candidate.createdAt ||
      candidate.reviewedAt ||
      inherited.generatedAt ||
      inherited.createdAt ||
      inherited.reviewedAt,
  };
}

function isSummaryCandidateCurrent(candidate, session = {}) {
  if (!candidate || typeof candidate !== "object") return false;
  const candidateSessionIds = [
    candidate.sessionId,
    candidate.sourceSessionId,
  ].map(normalizeText).filter(Boolean);
  const enclosingSessionId = normalizeText(
    session?.id || session?.sessionId || session?.createdAt
  );
  if (
    candidateSessionIds.some((candidateSessionId) => (
      !enclosingSessionId || candidateSessionId !== enclosingSessionId
    ))
  ) {
    return false;
  }
  if (
    candidate.current === false ||
    candidate.isCurrent === false ||
    candidate.active === false
  ) {
    return false;
  }
  const candidateStatuses = [
    candidate.generationStatus,
    candidate.status,
    candidate.reviewStatus,
  ].map(normalizeStatus).filter(Boolean);
  if (candidateStatuses.some((status) => NON_CURRENT_SUMMARY_STATUSES.has(status))) {
    return false;
  }
  if (normalizeStatus(candidate.persistenceStatus) === "failed") return false;
  const messages = Array.isArray(session?.messages) ? session.messages : [];
  const declaredMessageCount = Number(session?.messageCount);
  if (
    Number.isFinite(declaredMessageCount) &&
    declaredMessageCount > 0 &&
    declaredMessageCount !== messages.length
  ) {
    return false;
  }
  if (!messages.length) return true;
  return getSummaryFreshness(candidate, messages, {
    followUpFingerprint: sessionFollowUpFingerprint(session?.followUp),
  }).current;
}

function normalizedSummaryCandidate(value, forcedStatus = "", session = {}, versionSource = null) {
  const candidate = summaryCandidateWithVersion(value, versionSource);
  if (!candidate || !isSummaryCandidateCurrent(candidate, session)) return null;
  const candidateStatus = normalizeStatus(
    candidate.status ||
    candidate.reviewStatus ||
    candidate.generationStatus
  );
  const rawStatus = normalizeStatus(forcedStatus || candidateStatus);
  const text = preserveCanonicalSummaryText(
    candidate.summaryText ||
    candidate.reviewedSummaryText ||
    candidate.reviewedText ||
    candidate.text ||
    "",
    "",
    1200
  );
  if (!text) return null;
  const reviewed = Boolean(
    rawStatus === "reviewed" ||
    forcedStatus === "reviewed" ||
    candidate.reviewed === true ||
    candidate.reviewedAt
  );
  const source = normalizeText(candidate.source || candidate.model, 80);
  const fallbackLike = Boolean(
    candidate.fallbackUsed === true ||
    /(?:^|[\s_-])(?:fallback|draft|placeholder)(?:$|[\s_-])/i.test(source)
  );
  const status = reviewed
    ? "reviewed"
    : fallbackLike
        ? "draft"
        : rawStatus === "final"
          ? "final"
          : rawStatus || "canonical";
  const dependable = validatePostSessionProvenance({
    ...session,
    sessionSummary: candidate,
  }).trustedFinalSummary;
  const quality = reviewed ? 40 : status === "final" ? 30 : status === "canonical" ? 24 : 12;
  return {
    text,
    status,
    reviewed,
    dependable,
    source,
    generatedAt: toIsoTimestamp(candidate.generatedAt || candidate.createdAt || candidate.reviewedAt),
    quality,
  };
}

function selectPreferredSummary(
  session = {},
  { requireDependableSummary = false } = {}
) {
  const sessionSummary = session?.sessionSummary && typeof session.sessionSummary === "object"
    ? session.sessionSummary
    : null;
  const candidates = [
    normalizedSummaryCandidate(session?.reviewedSummary, "reviewed", session),
    normalizedSummaryCandidate(
      sessionSummary?.reviewedSummary,
      "reviewed",
      session,
      sessionSummary
    ),
    normalizedSummaryCandidate(
      sessionSummary?.reviewedSummaryText || sessionSummary?.reviewedText,
      "reviewed",
      session,
      sessionSummary
    ),
    normalizedSummaryCandidate(session?.canonicalSummary, "", session),
    normalizedSummaryCandidate(sessionSummary, "", session),
  ].filter(Boolean);
  const eligibleCandidates = requireDependableSummary
    ? candidates.filter((candidate) => candidate.dependable)
    : candidates;
  return eligibleCandidates.sort((left, right) => {
    if (right.quality !== left.quality) return right.quality - left.quality;
    return (Date.parse(right.generatedAt || "") || 0) - (Date.parse(left.generatedAt || "") || 0);
  })[0] || null;
}

function actionText(value) {
  if (typeof value === "string") return normalizeText(value, 280);
  if (!value || typeof value !== "object") return "";
  if (["dismissed", "cancelled"].includes(normalizeStatus(value.status))) {
    return "";
  }
  return normalizeText(
    value.text ||
    value.action ||
    value.nextStepText ||
    value.title ||
    value.name,
    280
  );
}

function actionCandidates(value) {
  if (Array.isArray(value)) return value;
  return value == null ? [] : [value];
}

function isCompletedAction(value) {
  return Boolean(
    value &&
    typeof value === "object" &&
    (
      value.completed === true ||
      normalizeStatus(value.status) === "completed" ||
      value.completedAt
    )
  );
}

function selectAgreedActions(session = {}, includeSummaryDerived = true) {
  const agreedCandidates = [
    session?.agreedAction,
    ...actionCandidates(session?.agreedActions),
    ...(includeSummaryDerived ? actionCandidates(session?.sessionSummary?.agreedActions) : []),
    ...(includeSummaryDerived ? actionCandidates(session?.sessionSummary?.actions) : []),
    ...(includeSummaryDerived ? actionCandidates(session?.sessionSummary?.coachingState?.actions) : []),
    ...actionCandidates(session?.plan?.assignments),
  ];
  const completedCandidates = [
    ...agreedCandidates.filter(isCompletedAction),
    session?.completedAction,
    ...actionCandidates(session?.completedActions),
    session?.completionAction,
    session?.coachingState?.completionAction,
    ...(includeSummaryDerived
      ? [
          session?.sessionSummary?.completedAction,
          ...actionCandidates(session?.sessionSummary?.completedActions),
          session?.sessionSummary?.completionAction,
          session?.sessionSummary?.coachingState?.completionAction,
        ]
      : []),
  ];
  const currentAgreedActions = agreedCandidates
    .filter((candidate) => !isCompletedAction(candidate))
    .map(actionText)
    .filter(Boolean);
  const completedActions = completedCandidates.map(actionText).filter(Boolean);
  return Array.from(new Set([
    ...currentAgreedActions.slice(0, 3),
    ...completedActions,
  ])).slice(0, 4);
}

function selectCurrentFollowUp(session = {}) {
  const followUp = session?.followUp;
  if (!followUp || typeof followUp !== "object") return null;
  const followUpSessionIds = [
    followUp.sessionId,
    followUp.sourceSessionId,
  ].map(normalizeText).filter(Boolean);
  const enclosingSessionId = normalizeText(
    session?.id || session?.sessionId || session?.createdAt
  );
  if (
    followUpSessionIds.some((followUpSessionId) => (
      !enclosingSessionId || followUpSessionId !== enclosingSessionId
    ))
  ) {
    return null;
  }
  const status = normalizeStatus(followUp.status || "suggested");
  const scheduled = Boolean(status === "scheduled" && followUp.scheduledFor);
  if (
    !scheduled &&
    (
      followUp.active === false ||
      followUp.isCurrent === false ||
      FINAL_FOLLOW_UP_STATUSES.has(status)
    )
  ) {
    return null;
  }
  const currentFollowUp = resolveDisplaySessionFollowUp(
    followUp,
    session?.sessionSummary || null,
    { messages: Array.isArray(session?.messages) ? session.messages : [] }
  );
  if (!currentFollowUp) return null;
  const nextStepText = actionText(currentFollowUp.nextStepText);
  const scheduledFor = toIsoTimestamp(currentFollowUp.scheduledFor);
  if (!nextStepText && !scheduledFor) return null;
  return {
    status: scheduledFor
      ? "scheduled"
      : normalizeStatus(currentFollowUp.status || status || "suggested"),
    nextStepText,
    scheduledFor,
    updatedAt: toIsoTimestamp(currentFollowUp.updatedAt),
  };
}

function selectedThemes(session = {}, includeSummaryDerived = true) {
  const themes = includeSummaryDerived && Array.isArray(session?.sessionSummary?.detectedThemes)
    ? session.sessionSummary.detectedThemes
    : [];
  return Array.from(new Set(themes.map((theme) => normalizeText(theme, 80)).filter(Boolean))).slice(0, 5);
}

function operationalComplaintScore(
  session = {},
  searchableText = "",
  includeSummaryDerived = true
) {
  const explicitlyOperational = Boolean(
    session?.operationalComplaint === true ||
    session?.operationalIntent?.complaint === true ||
    (
      includeSummaryDerived &&
      (
        session?.sessionSummary?.operationalComplaint === true ||
        session?.sessionSummary?.operationalIntent?.complaint === true
      )
    )
  );
  const structuredRoute = [
    session?.responseRoute,
    session?.intent,
    session?.intentClassification,
    ...(includeSummaryDerived
      ? [
          session?.sessionSummary?.responseRoute,
          session?.sessionSummary?.intent,
          session?.sessionSummary?.intentClassification,
        ]
      : []),
  ].map(normalizeStatus).find((value) => OPERATIONAL_ROUTE_PATTERN.test(value));
  const textSignalsComplaint = (
    OPERATIONAL_RECORD_SUBJECT_PATTERN.test(searchableText) &&
    OPERATIONAL_FAILURE_PATTERN.test(searchableText)
  );
  return (
    (explicitlyOperational ? 100 : 0) +
    (structuredRoute ? 70 : 0) +
    (textSignalsComplaint ? 50 : 0)
  );
}

function packetItemForSession(
  session = {},
  {
    requestTokens = [],
    requireOperationalComplaint = false,
    requireDependableSummary = false,
  } = {}
) {
  const sessionSummaryCurrent = isSummaryCandidateCurrent(
    summaryCandidateWithVersion(session?.sessionSummary),
    session
  );
  const summary = selectPreferredSummary(session, {
    requireDependableSummary,
  });
  if (requireDependableSummary && !summary) return null;
  const agreedActions = selectAgreedActions(session, sessionSummaryCurrent);
  const followUp = selectCurrentFollowUp(session);
  const themes = selectedThemes(session, sessionSummaryCurrent);
  if (!summary && !agreedActions.length && !followUp) return null;
  const searchableText = [
    summary?.text,
    themes.join(" "),
    agreedActions.join(" "),
    followUp?.nextStepText,
  ].filter(Boolean).join(" ");
  const complaintScore = operationalComplaintScore(
    session,
    searchableText,
    sessionSummaryCurrent
  );
  if (requireOperationalComplaint && !complaintScore) return null;
  const contentTokens = new Set(topicTokens(searchableText));
  const overlap = requestTokens.filter((token) => contentTokens.has(token));
  if (requestTokens.length && !overlap.length) return null;
  const recency = getSessionRecencyTimestamp(session);
  const relevanceScore =
    overlap.length * 50 +
    Number(summary?.quality || 0) +
    (agreedActions.length ? 16 : 0) +
    (followUp?.status === "scheduled" ? 24 : followUp ? 10 : 0) +
    (requireOperationalComplaint ? complaintScore : 0);
  return {
    item: {
      sessionId: normalizeText(session?.id || session?.sessionId || session?.createdAt, 120),
      occurredAt: toIsoTimestamp(session?.createdAt || session?.startedAt || session?.date || session?.dateISO),
      summary: summary
        ? {
            text: summary.text,
            status: summary.status,
            reviewed: summary.reviewed,
            source: summary.source,
            generatedAt: summary.generatedAt,
          }
        : null,
      themes,
      agreedActions,
      currentFollowUp: followUp,
    },
    summaryQuality: Number(summary?.quality || 0),
    relevanceScore,
    recency,
  };
}

export function detectContinuityRetrievalIntent(value = "") {
  const text = normalizeText(value);
  if (!text) return { triggered: false, kind: "none" };
  const continuity = (
    CONTINUITY_REFERENCE_PATTERN.test(text) ||
    CONTINUITY_RETRIEVAL_PATTERN.test(text) ||
    CONTINUITY_REENTRY_PATTERN.test(text)
  );
  const escalation = ESCALATION_RETRIEVAL_PATTERN.test(text);
  return {
    triggered: continuity || escalation,
    kind: continuity && escalation
      ? "continuity_escalation"
      : continuity
        ? "continuity"
        : escalation
          ? "escalation"
          : "none",
  };
}

export function shouldRetrieveSameUserContinuity(value = "") {
  return detectContinuityRetrievalIntent(value).triggered;
}

function requestsExactMostRecentSession(value = "") {
  return EXACT_MOST_RECENT_REFERENCE_PATTERN.test(normalizeText(value));
}

export function buildSameUserContinuityPacket({
  sessions = [],
  orgId = "",
  userId = "",
  currentSessionId = "",
  requestText = "",
  maxSessions = CONTINUITY_PACKET_MAX_SESSIONS,
} = {}) {
  const safeOrgId = normalizeText(orgId);
  const safeUserId = normalizeText(userId);
  const activeSessionId = normalizeText(currentSessionId);
  const trigger = detectContinuityRetrievalIntent(requestText);
  const unavailable = (reason) => ({
    available: false,
    reason,
    trigger,
    sessions: [],
  });
  if (!safeOrgId || !safeUserId || !Array.isArray(sessions)) return unavailable("missing_scope");
  if (!trigger.triggered) return unavailable("not_requested");

  const requireOperationalComplaint = trigger.kind === "escalation";
  const limit = Math.max(1, Math.min(
    CONTINUITY_PACKET_MAX_SESSIONS,
    Number(maxSessions) || CONTINUITY_PACKET_MAX_SESSIONS
  ));
  const scopedSessions = sessions.filter((session) => {
    const sessionId = normalizeText(session?.id || session?.sessionId || session?.createdAt);
    return Boolean(
      sessionId &&
      (!activeSessionId || sessionId !== activeSessionId) &&
      normalizeText(session?.orgId) === safeOrgId &&
      normalizeText(session?.userId) === safeUserId
    );
  });
  const exactMostRecentRequested = requestsExactMostRecentSession(requestText);
  const requestTokens =
    requireOperationalComplaint || exactMostRecentRequested
      ? []
      : topicTokens(requestText);
  let candidateSessions = scopedSessions;
  if (exactMostRecentRequested) {
    const timestamped = scopedSessions.map((session) => ({
      session,
      recency: getSessionConversationRecencyTimestamp(session),
    }));
    if (!timestamped.length) return unavailable("no_relevant_same_user_context");
    if (
      timestamped.length > 1 &&
      timestamped.some(({ recency }) => !Number.isFinite(recency) || recency <= 0)
    ) {
      return unavailable("most_recent_same_user_session_unverifiable");
    }
    timestamped.sort((left, right) => right.recency - left.recency);
    if (timestamped.length > 1 && timestamped[0].recency === timestamped[1].recency) {
      return unavailable("most_recent_same_user_session_unverifiable");
    }
    candidateSessions = [timestamped[0].session];
  }

  const ranked = candidateSessions
    .map((session) => packetItemForSession(session, {
      requestTokens,
      requireOperationalComplaint,
      requireDependableSummary: exactMostRecentRequested,
    }))
    .filter(Boolean)
    .sort((left, right) => (
      right.summaryQuality - left.summaryQuality ||
      right.relevanceScore - left.relevanceScore ||
      right.recency - left.recency
    ))
    .slice(0, exactMostRecentRequested ? 1 : limit)
    .map(({ item }) => item);

  if (!ranked.length) return unavailable("no_relevant_same_user_context");
  return {
    available: true,
    reason: "matched_same_user_context",
    trigger,
    sessions: ranked,
  };
}

function hasUserMessage(session = {}) {
  if (Number(session.messageCount || 0) > 1) return true;
  const messages = Array.isArray(session.messages) ? session.messages : [];
  return messages.some((message) => String(message?.role || message?.sender || "").toLowerCase() !== "assistant");
}

export function findResumableOwnSession(sessions = [], userId = "", nowMs = Date.now()) {
  const uid = String(userId || "");
  if (!uid || !Array.isArray(sessions)) return null;
  return sortSessionsByRecency(sessions).find((session) => {
    const id = String(session?.id || session?.createdAt || "").trim();
    if (!id) return false;
    if (String(session?.userId || "") !== uid) return false;
    if (!hasUserMessage(session)) return false;
    const recency = getSessionRecencyTimestamp(session);
    if (!recency) return false;
    return nowMs - recency <= RESUMABLE_SESSION_MAX_AGE_MS;
  }) || null;
}

export const findResumableMemberSession = findResumableOwnSession;

export function getChatExitTarget() {
  return "/dashboard";
}
